import { useState } from 'react';
import { formatDateSafe } from '@/utils/dateUtils';
import { computeReturnDate, computeEndDate } from '../../utils/vacationRules';
import { buildScheduleLimitMessage } from '../../utils/vacationStartDateRules';
import { useHolidayDatesQuery } from '../../hooks/data/useHolidaysQuery';
import { useCorrectionDateBounds } from './hooks/useCorrectionDateBounds';
import { IsoDatePicker } from '../VacationRequestForm/components/IsoDatePicker';
import { SEGMENT_CORRECTION_LABELS, resolveCorrectionErrorMessage } from './segmentCorrectionControl.definitions';
import type { SegmentCorrectionControlProps } from './segmentCorrectionControl.types';

export function SegmentCorrectionControl({
    segment,
    request,
    holidays: passedHolidays,
    isSubmitting = false,
    onSubmit,
    onDraftChange,
}: SegmentCorrectionControlProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [selectedDate, setSelectedDate] = useState(segment.startDate);
    const [error, setError] = useState<string | null>(null);
    const { holidayDates } = useHolidayDatesQuery();

    const holidays = passedHolidays?.length ? passedHolidays : holidayDates;
    const { isDateSelectable, defaultMonthIso } = useCorrectionDateBounds({
        request,
        segment,
        holidays,
    });

    if (!isEditing) {
        return (
            <div className="flex justify-center">
                <button
                    type="button"
                    onClick={() => {
                        setIsEditing(true);
                        setError(null);
                    }}
                    className="inline-flex items-center gap-2 rounded-xl bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600 transition-colors font-montserrat"
                >
                    {SEGMENT_CORRECTION_LABELS.BUTTON_CLOSED}
                </button>
            </div>
        );
    }

    const newEndDate = computeEndDate(selectedDate, segment.days);
    const returnDate = computeReturnDate(selectedDate, segment.days);

    const reportDraft = (isoDate: string) => {
        if (!onDraftChange) return;
        onDraftChange(isDateSelectable(isoDate) ? { startDate: isoDate, endDate: computeEndDate(isoDate, segment.days) } : null);
    };

    const handleSubmit = async () => {
        setError(null);
        if (!isDateSelectable(selectedDate)) {
            setError(SEGMENT_CORRECTION_LABELS.ERROR_INVALID_DATE);
            return;
        }

        try {
            await onSubmit?.(selectedDate, newEndDate);
            setIsEditing(false);
        } catch (err) {
            setError(resolveCorrectionErrorMessage(err));
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <label htmlFor="correction-start" className="block text-sm font-medium text-raro-gray-700 font-montserrat">
                    {SEGMENT_CORRECTION_LABELS.LABEL_START_DATE}
                </label>
                <div className="mt-1">
                    <IsoDatePicker
                        id="correction-start"
                        value={selectedDate}
                        onChange={(iso) => {
                            setSelectedDate(iso);
                            setError(null);
                            reportDraft(iso);
                        }}
                        disabled={isSubmitting}
                        isDateSelectable={isDateSelectable}
                        defaultMonthIso={defaultMonthIso}
                    />
                </div>
                {request.concessiveDeadline && (
                    <p className="mt-1 text-xs text-amber-700 font-montserrat">
                        {buildScheduleLimitMessage(request.concessiveDeadline)}
                    </p>
                )}
                {error && (
                    <p className="mt-1 text-xs text-red-600 font-montserrat">{error}</p>
                )}
            </div>

            <div>
                <label htmlFor="correction-end" className="block text-sm font-medium text-raro-gray-700 font-montserrat">
                    Nova data de fim
                </label>
                <input
                    id="correction-end"
                    type="text"
                    value={formatDateSafe(newEndDate)}
                    readOnly
                    disabled
                    className="mt-1 block w-full rounded-xl border border-primary-200 bg-gray-50 p-2.5 text-sm font-montserrat text-raro-gray-500"
                />
            </div>

            <div className="flex gap-2 pt-2 justify-center">
                {onSubmit && !onDraftChange && (
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="px-4 py-2 text-sm font-semibold bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 font-montserrat transition-colors"
                    >
                        {isSubmitting ? SEGMENT_CORRECTION_LABELS.BUTTON_SUBMITTING : SEGMENT_CORRECTION_LABELS.BUTTON_SUBMIT}
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => {
                        setIsEditing(false);
                        setSelectedDate(segment.startDate);
                        setError(null);
                        onDraftChange?.(null);
                    }}
                    disabled={isSubmitting}
                    className="px-4 py-2 text-sm font-semibold border border-raro-gray-200 text-raro-gray-700 rounded-lg hover:border-raro-gray-300 disabled:opacity-50 font-montserrat transition-colors"
                >
                    {SEGMENT_CORRECTION_LABELS.BUTTON_CANCEL}
                </button>
            </div>
        </div>
    );
}
